import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import {
  OutputFormulaValue,
  OutputFormulaValueKind,
} from 'src/app/models/output-formulas/output-formula-value';
import {
  OutputFormulaWriteReason,
  OutputFormulaWriteReasonKind,
} from 'src/app/models/output-formulas/output-formula-write-reason';
import { filterRangeConventions } from './filename-convention-filters';
import { filterStillsSpecs, filterVideosSpecs } from './file-specification-filters';

export interface OutputFormulaRangeUpdateDialogData {
  organizationId: string;
  current: OutputFormula;
  fileSpecifications: FileSpecification[];
  filenameConventions: FilenameConvention[];
}

type RangeKind = Extract<OutputFormulaValueKind, 'rangeStills' | 'rangeVideos' | 'rangeBoth'>;
type UpdateReasonKind = Exclude<OutputFormulaWriteReasonKind, 'initialCreate' | 'deleted'>;

const SELECTABLE_REASONS: readonly UpdateReasonKind[] = [
  'preferenceUpdate',
  'correction',
  'other',
];

const REASON_LABELS: Record<UpdateReasonKind, string> = {
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  other: 'Other',
};

@Component({
  selector: 'app-output-formula-range-update-dialog',
  templateUrl: './output-formula-range-update-dialog.component.html',
  styleUrls: ['./output-formula-range-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OutputFormulaRangeUpdateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectableReasons = SELECTABLE_REASONS;
  readonly reasonLabel = (k: UpdateReasonKind): string => REASON_LABELS[k];

  readonly nameControl: FormControl<string>;
  readonly kind: RangeKind;

  readonly stillsSpecIdControl = new FormControl<string | null>(null);
  readonly stillsConventionIdControl = new FormControl<string | null>(null);
  readonly videosSpecIdControl = new FormControl<string | null>(null);
  readonly videosConventionIdControl = new FormControl<string | null>(null);

  readonly reasonControl = new FormControl<UpdateReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutputFormulaRangeUpdateDialogData,
    private readonly dialogRef: MatDialogRef<
      OutputFormulaRangeUpdateDialogComponent,
      OutputFormula | undefined
    >,
    private readonly service: OutputFormulaService,
  ) {
    const current = data.current;
    this.nameControl = new FormControl<string>(current.name, {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    });
    this.kind = this.assertRangeKind(current.value.kind);
    this.seedSlotControls(current.value);
  }

  private assertRangeKind(kind: OutputFormulaValueKind): RangeKind {
    if (kind === 'rangeStills' || kind === 'rangeVideos' || kind === 'rangeBoth') return kind;
    throw new Error(`OutputFormulaRangeUpdateDialog opened with non-range kind: ${kind}`);
  }

  private seedSlotControls(value: OutputFormulaValue): void {
    switch (value.kind) {
      case 'rangeStills':
        this.stillsSpecIdControl.setValue(value.stills.specId);
        this.stillsConventionIdControl.setValue(value.stills.conventionId);
        break;
      case 'rangeVideos':
        this.videosSpecIdControl.setValue(value.videos.specId);
        this.videosConventionIdControl.setValue(value.videos.conventionId);
        break;
      case 'rangeBoth':
        this.stillsSpecIdControl.setValue(value.stills.specId);
        this.stillsConventionIdControl.setValue(value.stills.conventionId);
        this.videosSpecIdControl.setValue(value.videos.specId);
        this.videosConventionIdControl.setValue(value.videos.conventionId);
        break;
      default:
        break;
    }
  }

  get stillsFileSpecifications(): FileSpecification[] {
    return filterStillsSpecs(this.data.fileSpecifications);
  }

  get videosFileSpecifications(): FileSpecification[] {
    return filterVideosSpecs(this.data.fileSpecifications);
  }

  get rangeConventions(): FilenameConvention[] {
    return filterRangeConventions(this.data.filenameConventions);
  }

  get needsStillsSlot(): boolean {
    return this.kind === 'rangeStills' || this.kind === 'rangeBoth';
  }

  get needsVideosSlot(): boolean {
    return this.kind === 'rangeVideos' || this.kind === 'rangeBoth';
  }

  get isOther(): boolean {
    return this.reasonControl.value === 'other';
  }

  get canSave(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.needsStillsSlot) {
      if (this.stillsSpecIdControl.value === null) return false;
      if (this.stillsConventionIdControl.value === null) return false;
    }
    if (this.needsVideosSlot) {
      if (this.videosSpecIdControl.value === null) return false;
      if (this.videosConventionIdControl.value === null) return false;
    }
    if (this.reasonControl.value === null) return false;
    if (this.isOther && !this.detailControl.valid) return false;
    return true;
  }

  save(): void {
    if (!this.canSave) return;
    const writeReason = this.buildWriteReason();
    const value = this.buildValue();
    if (writeReason === null || value === null) return;
    this.submitting = true;
    this.service
      .update(this.data.organizationId, this.data.current.id, {
        name: this.nameControl.value.trim(),
        value,
        writeReason,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: OutputFormula) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildWriteReason(): OutputFormulaWriteReason | null {
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') return { kind: 'other', detail: this.detailControl.value.trim() };
    return { kind };
  }

  private buildValue(): OutputFormulaValue | null {
    switch (this.kind) {
      case 'rangeStills': {
        const specId = this.stillsSpecIdControl.value;
        const conventionId = this.stillsConventionIdControl.value;
        if (specId === null || conventionId === null) return null;
        return { kind: 'rangeStills', stills: { specId, conventionId } };
      }
      case 'rangeVideos': {
        const specId = this.videosSpecIdControl.value;
        const conventionId = this.videosConventionIdControl.value;
        if (specId === null || conventionId === null) return null;
        return { kind: 'rangeVideos', videos: { specId, conventionId } };
      }
      case 'rangeBoth': {
        const stillsSpecId = this.stillsSpecIdControl.value;
        const stillsConventionId = this.stillsConventionIdControl.value;
        const videosSpecId = this.videosSpecIdControl.value;
        const videosConventionId = this.videosConventionIdControl.value;
        if (
          stillsSpecId === null || stillsConventionId === null ||
          videosSpecId === null || videosConventionId === null
        ) return null;
        return {
          kind: 'rangeBoth',
          stills: { specId: stillsSpecId, conventionId: stillsConventionId },
          videos: { specId: videosSpecId, conventionId: videosConventionId },
        };
      }
    }
  }
}
